import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Keeps a page's active-tab state in sync with the `?tab=` URL param — direct
// links, bookmarks, and browser back/forward all land on the right tab, and
// clicking a tab pushes a real history entry. Falls back to `defaultTab` (and
// rewrites the URL to match) when `?tab=` is missing or not in `validTabs`.
export function useUrlSyncedTab<T extends string>(validTabs: readonly T[], defaultTab: T): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState<T>(() => {
    const fromUrl = searchParams.get("tab");
    return fromUrl && (validTabs as readonly string[]).includes(fromUrl) ? (fromUrl as T) : defaultTab;
  });

  useEffect(() => {
    const fromUrl = searchParams.get("tab");
    if (!fromUrl) return;
    if ((validTabs as readonly string[]).includes(fromUrl)) {
      if (fromUrl !== tab) setTabState(fromUrl as T);
    } else {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set("tab", tab);
        return p;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function setTab(next: T) {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set("tab", next);
      return p;
    });
  }

  return [tab, setTab];
}
